// src/components/Layout/Navbar.tsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar: React.FC = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'Who We Are' },
    { path: '/programs', label: 'What We Do' },
    { path: '/carbon-credit', label: 'Carbon Credit' },
    { path: '/blog', label: 'Blog' },
    { path: '/volunteer', label: 'Volunteer' },
    { path: '/contact', label: 'Contact Us' },
  ];

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav 
      className={`navbar navbar-expand-lg fixed-top transition-all ${
        isScrolled 
          ? 'navbar-scrolled bg-white shadow-sm' 
          : 'navbar-transparent bg-light'
      }`}
    >
      <div className="container">
        {/* Brand Logo */}
        <Link 
          className="navbar-brand fw-bold d-flex align-items-center" 
          to="/"
        >
          <div className="logo-container position-relative">
            {/* Light logo - visible when not scrolled */}
            <img 
              src="/images/logo.png" 
              alt="Impact Generation Africa" 
              height="40"
              width="40"
              className={`logo-light transition-all ${
                isScrolled ? 'opacity-0' : 'opacity-100'
              }`}
              onError={(e) => {
                // Fallback to filter if light logo doesn't exist
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            {/* Original logo - visible when scrolled */}
            <img 
              src="/images/logo.png" 
              alt="Impact Generation Africa" 
              height="40"
              width="40"
              className={`logo-original position-absolute top-0 start-0 transition-all ${
                isScrolled ? 'opacity-100' : 'opacity-0'
              }`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            {/* Fallback - single logo with filter */}
            <img 
              src="/images/logo.png" 
              alt="Impact Generation Africa" 
              height="40"
              width="40"
              className="logo-fallback"
              style={{
                filter: isScrolled ? 'none' : 'brightness(0) saturate(100%) invert(30%) sepia(10%) saturate(500%) hue-rotate(180deg)',
                display: 'none' // Hidden by default, shown via onError
              }}
              onError={(e) => {
                // Show fallback if both logos fail to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'block';
              }}
            />
          </div>
          <span className={`ms-2 ${isScrolled ? 'text-gray-800' : 'text-gray-700'}`}>
            Impact Generation Africa
          </span>
        </Link>

        {/* Mobile Toggle Button */}
        <button 
          className="navbar-toggler border-0"
          type="button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle navigation"
        >
          <span className={`navbar-toggler-icon ${isScrolled ? 'navbar-toggler-dark' : ''}`}></span>
        </button>

        {/* Navigation Items */}
        <div 
          className={`collapse navbar-collapse ${isMobileMenuOpen ? 'show' : ''}`}
          id="navbarNav"
        >
          <ul className="navbar-nav ms-auto align-items-lg-center">
            {navItems.map((item) => (
              <li key={item.path} className="nav-item mx-lg-1">
                <Link 
                  className={`nav-link position-relative px-3 py-2 rounded ${
                    isActivePath(item.path) 
                      ? isScrolled ? 'active-scrolled' : 'active' 
                      : isScrolled ? 'text-gray-700' : 'text-gray-600'
                  } hover-effect`}
                  to={item.path}
                >
                  {item.label}
                  {isActivePath(item.path) && (
                    <span className="active-indicator"></span>
                  )}
                </Link>
              </li>
            ))}
            
            {/* Donate Button */}
            <li className="nav-item ms-lg-2 mt-2 mt-lg-0">
              <Link 
                className={`btn btn-lg fw-semibold px-4 py-2 donate-button ${
                  isScrolled 
                    ? 'btn-primary text-white' 
                    : 'btn-outline-primary text-primary'
                } shadow-sm hover-lift`}
                to="/donate"
              >
                💚 Donate
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Custom Styles */}
      <style>{`
        .navbar {
          transition: all 0.3s ease;
          padding: 1rem 0;
        }
        
        .navbar-transparent {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }
        
        .navbar-scrolled {
          padding: 0.5rem 0;
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.98) !important;
        }
        
        .logo-container {
          height: 40px;
          width: 40px;
        }
        
        .logo-light,
        .logo-original,
        .logo-fallback {
          position: absolute;
          top: 0;
          left: 0;
          transition: opacity 0.3s ease;
        }
        
        .logo-light {
          opacity: 1;
          filter: brightness(0) saturate(100%) invert(30%) sepia(10%) saturate(500%) hue-rotate(180deg);
        }
        
        .logo-original {
          opacity: 0;
        }
        
        .navbar-scrolled .logo-light {
          opacity: 0;
        }
        
        .navbar-scrolled .logo-original {
          opacity: 1;
        }
        
        .navbar-toggler-icon {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(74, 85, 104, 0.7)' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
        }
        
        .navbar-toggler-dark .navbar-toggler-icon {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(74, 85, 104, 0.7)' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
        }
        
        .nav-link.active {
          background: rgba(74, 85, 104, 0.1) !important;
          color: #4a5568 !important;
          font-weight: 600;
        }
        
        .nav-link.active-scrolled {
          color: #4a5568 !important;
          background: rgba(74, 85, 104, 0.1) !important;
          font-weight: 600;
        }
        
        .active-indicator {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          background: #4a5568;
          border-radius: 50%;
        }
        
        .hover-effect {
          transition: all 0.2s ease;
          border-radius: 6px;
        }
        
        .hover-effect:hover {
          transform: translateY(-1px);
          background: rgba(74, 85, 104, 0.08);
          color: #2d3748 !important;
        }
        
        .text-gray-700.hover-effect:hover {
          background: rgba(74, 85, 104, 0.08);
        }
        
        .donate-button {
          border-radius: 8px;
          transition: all 0.3s ease;
          border: 2px solid;
          font-size: 0.95rem;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
          border-color: #48bb78;
        }
        
        .btn-outline-primary {
          border-color: #48bb78;
          color: #48bb78;
          background: transparent;
        }
        
        .btn-outline-primary:hover {
          background: #48bb78;
          color: white;
        }
        
        .donate-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(72, 187, 120, 0.2);
        }
        
        .hover-lift:hover {
          transform: translateY(-2px);
        }
        
        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .navbar,
          .logo-light,
          .logo-original,
          .logo-fallback,
          .hover-effect,
          .donate-button {
            transition: none;
          }
          
          .hover-effect:hover,
          .donate-button:hover {
            transform: none;
          }
        }
        
        /* High contrast support */
        @media (prefers-contrast: high) {
          .navbar-transparent {
            background: #ffffff !important;
            border-bottom: 2px solid #000;
          }
          
          .nav-link {
            font-weight: 600;
          }
          
          .nav-link.active,
          .nav-link.active-scrolled {
            background: #000 !important;
            color: #fff !important;
          }
          
          .active-indicator {
            background: #000;
          }
        }
        
        /* Mobile responsive improvements */
        @media (max-width: 991.98px) {
          .navbar-collapse {
            background: white;
            margin: 1rem -1rem -1rem -1rem;
            padding: 1rem;
            border-radius: 0 0 10px 10px;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
            border: 1px solid rgba(0, 0, 0, 0.05);
          }
          
          .nav-link {
            color: #4a5568 !important;
            margin: 0.25rem 0;
            font-weight: 500;
          }
          
          .nav-link.active,
          .nav-link.active-scrolled {
            background: rgba(74, 85, 104, 0.1) !important;
            color: #2d3748 !important;
          }
          
          .donate-button {
            width: 100%;
            text-align: center;
            margin-top: 0.5rem;
          }
        }
        
        /* Large screen optimizations */
        @media (min-width: 992px) {
          .nav-link {
            font-size: 0.95rem;
            margin: 0 0.25rem;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;